import { QueryClientProvider } from '@tanstack/react-query';
import type { PageProps } from 'gatsby';
import type { PropsWithChildren, ReactElement } from 'react';
import React from 'react';
import Helmet from 'react-helmet';

import { AbbyChatBotIcon } from 'src/components/common/abby-chat/abby-chat-icon';
import type { PageContext, PageLayoutOptions } from 'src/components/common/page-context';
import { PageLayoutType } from 'src/components/common/page-context';
import {
  LocalisedErrorBoundary,
  StaticErrorBoundary,
} from 'src/components/common/page-error-boundary';
import type { TemplatePageContext } from 'src/components/common/template-page-context';
import ClubPageLayout from 'src/components/layout/club-page-layout';
import GlobalPageLayout from 'src/components/layout/global-page-layout';
import { SimplePageLayout } from 'src/components/layout/simple-page-layout';
import SiteProvider from 'src/context/site-provider';
import { useRouteUpdateHandler } from 'src/custom-hooks/use-route-update-handler';
import { ADMIN_BASE_PATH } from 'src/helpers/admin-path';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import queryClient from 'src/query-client';

import CareerPageLayout from './career-page-layout';

type LayoutProps = PropsWithChildren<{
  props: PageProps<unknown, PageContext | TemplatePageContext, unknown>;
}>;

const PageLayout = ({ props, children }: LayoutProps) => {
  const pageContext =
    (props.pageContext as TemplatePageContext).baseContext ?? (props.pageContext as PageContext);

  const pageLayout: PageLayoutOptions =
    props.location.pathname === ADMIN_BASE_PATH
      ? { type: PageLayoutType.blank }
      : pageContext.pageLayout;

  useRouteUpdateHandler({ buildType: pageContext.buildType, pathname: props.location.pathname });

  const errorBoundaryWrappedChildren = <LocalisedErrorBoundary>{children}</LocalisedErrorBoundary>;

  const page: ReactElement = (() => {
    switch (pageLayout.type) {
      case PageLayoutType.blank:
        return <>{errorBoundaryWrappedChildren}</>;
      case PageLayoutType.simple:
        return (
          <SimplePageLayout
            shouldShowFooter={pageLayout.shouldShowFooter ?? false}
            globalCmsPageData={props.data}
          >
            {errorBoundaryWrappedChildren}
          </SimplePageLayout>
        );
      case PageLayoutType.global:
        return (
          <GlobalPageLayout globalCmsPageData={props.data}>
            {errorBoundaryWrappedChildren}
          </GlobalPageLayout>
        );
      case PageLayoutType.club:
        if (pageContext.isGlobalPage) {
          throw new Error('Club page layout requires a club page context with a clubId');
        }
        return (
          <ClubPageLayout
            siteId={pageContext.clubId}
            clubAndClubFinderCmsPageData={props.data}
            globalCmsPageData={props.data}
          >
            {errorBoundaryWrappedChildren}
          </ClubPageLayout>
        );
      case PageLayoutType.careers:
        return (
          <CareerPageLayout globalCmsPageData={props.data}>
            {errorBoundaryWrappedChildren}
          </CareerPageLayout>
        );
    }
  })();

  return (
    <StaticErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Helmet>
          <meta name="format-detection" content="telephone=no" />
        </Helmet>
        <SiteProvider pageContext={pageContext}>
          <div
            className={`brand-${getBrandForBuildType(pageContext.buildType)}`}
            style={{ display: 'contents' }}
          >
            {page}
          </div>
        </SiteProvider>
        <AbbyChatBotIcon />
      </QueryClientProvider>
    </StaticErrorBoundary>
  );
};

export default PageLayout;
